// Shared sourceAction classification, reused by every Handbook domain builder
// that has adopted source-routed editing (currently DM_02, DM_03, DM_05). This
// is the one piece that is genuinely identical across domains: given a real
// Knowledge fact's id and sourceRefs, decide whether it is a caregiver-owned
// fact or an external record, and build the presentation-safe sourceAction
// object. Everything else - which facts are even eligible, how they're
// classified into groups, how they're ranked and worded - stays in each
// domain's own file and must not be centralized here.

import { isCaregiverAuthoredProfileFact } from '../knowledgeItems/caregiverProfileFact';

const EXTERNAL_DOCUMENT_SOURCE_TYPES = new Set([
  'care_knowledge_document',
  'care_knowledge_photo',
  'care_knowledge_screenshot',
  'profile_document',
  'doctor_note',
  'file_import',
  'ocr_text',
  'email',
  'incoming_email',
  'forwarded_email',
]);

const CAREGIVER_SOURCE_TYPES = new Set([
  'care_knowledge',
  'typed_long_note',
  'quick_check_in',
  'dictated_text',
  'voice',
]);

const hasViewableSourceContent = (sourceRefs = []) => (
  sourceRefs.some((ref) => ref?.sourceContext || ref?.sourceText || ref?.sourceTitle)
);

/**
 * Attaches sourceAction only when ownership is unambiguous for a real
 * Knowledge fact. Callers are responsible for excluding synthetic/legacy
 * items *before* calling this (there is nothing a Knowledge write path could
 * update for those) - this function assumes `id`/`sourceRefs` already belong
 * to a real Knowledge doc.
 */
export const resolveKnowledgeSourceAction = (fact = {}) => {
  const { id, sourceRefs = [] } = fact;
  if (!id) return undefined;

  const refs = Array.isArray(sourceRefs) ? sourceRefs : [];
  const sourceTypes = refs.map((ref) => ref?.sourceType).filter(Boolean);
  const hasExternalDocument = sourceTypes.some((type) => EXTERNAL_DOCUMENT_SOURCE_TYPES.has(type));

  if (hasExternalDocument) {
    const documentRef = refs.find((ref) => EXTERNAL_DOCUMENT_SOURCE_TYPES.has(ref?.sourceType));
    return {
      ownership: 'external_record',
      actionId: id,
      actionLabel: 'View source',
      destination: 'document_view',
      sourceLabel: documentRef?.sourceTitle || 'Uploaded document',
      // The existing caregiver-text capture path (CareKnowledgeCaptureCard ->
      // saveTextCareSourceForProcessing -> extraction pipeline) has no way for
      // the UI to attach a caller-supplied sourceRef linking a new fact back to
      // this one, so a note here could not actually be distinguished from an
      // unrelated fact. Keep this false until that linkage exists, rather than
      // advertising a capability the current write path cannot honor.
      allowsAnnotation: false,
      isEditable: false,
      isAvailable: hasViewableSourceContent(refs),
    };
  }

  if (isCaregiverAuthoredProfileFact(fact)) {
    return {
      ownership: 'caregiver_fact',
      actionId: id,
      actionLabel: 'Edit',
      destination: 'knowledge_edit',
      sourceLabel: null,
      allowsAnnotation: false,
      isEditable: true,
      isAvailable: true,
    };
  }

  const caregiverSourceRef = refs.find((ref) => CAREGIVER_SOURCE_TYPES.has(ref?.sourceType));
  if (caregiverSourceRef && hasViewableSourceContent(refs)) {
    return {
      ownership: 'external_record',
      actionId: id,
      actionLabel: 'View source',
      destination: 'source_view',
      sourceLabel: caregiverSourceRef.sourceTitle || 'Caregiver note',
      allowsAnnotation: false,
      isEditable: false,
      isAvailable: true,
    };
  }

  // Unrecognized/unknown sourceType (e.g. 'incoming_message', 'unknown') -
  // do not guess an action.
  return undefined;
};
